import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'

// Helper function to format Date object to YYYY-MM-DD string
function formatDateToString(date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

// Helper function to get today's date as YYYY-MM-DD string
function getTodayString() {
  const today = new Date()
  return formatDateToString(today)
}

// Helper function to parse date string to Date object (local time)
function parseDateString(dateString) {
  if (!dateString) return new Date()
  const [year, month, day] = dateString.split('-').map(Number)
  return new Date(year, month - 1, day) // month is 0-indexed
}

const DatePicker = ({ 
  value, 
  onChange, 
  placeholder = "日付を選択",
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedDate, setSelectedDate] = useState(() => value || getTodayString())
  const [currentMonth, setCurrentMonth] = useState(() => {
    // Initialize currentMonth based on the value prop
    if (value) {
      const date = parseDateString(value)
      return new Date(date.getFullYear(), date.getMonth(), 1)
    }
    return new Date()
  })
  const [showYearPicker, setShowYearPicker] = useState(false)
  const [showMonthPicker, setShowMonthPicker] = useState(false)
  const datePickerRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false)
        setShowYearPicker(false)
        setShowMonthPicker(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Only update internal state when value prop actually changes
  useEffect(() => {
    if (value && value !== selectedDate) {
      setSelectedDate(value)
      // Update currentMonth to match the selected date
      const date = parseDateString(value)
      if (!isNaN(date.getTime())) {
        setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1))
      }
    }
  }, [value, selectedDate])

  // Memoize formatDate to prevent unnecessary recalculations
  const formatDate = useCallback((dateString) => {
    if (!dateString) return ''
    try {
      const date = parseDateString(dateString)
      if (isNaN(date.getTime())) return dateString
      
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const weekdays = ['日', '月', '火', '水', '木', '金', '土']
      const weekday = weekdays[date.getDay()]
      return `${year}/${month}/${day}(${weekday})`
    } catch (error) {
      console.error('Error formatting date:', error)
      return dateString
    }
  }, [])

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  }

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay()
  }

  const handleDateSelect = useCallback((day) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
    const formattedDate = formatDateToString(newDate)
    setSelectedDate(formattedDate)
    setIsOpen(false)
    onChange(formattedDate)
  }, [currentMonth, onChange])

  const handleMonthChange = useCallback((direction) => {
    const newMonth = new Date(currentMonth)
    if (direction === 'prev') {
      newMonth.setMonth(newMonth.getMonth() - 1)
    } else {
      newMonth.setMonth(newMonth.getMonth() + 1)
    }
    setCurrentMonth(newMonth)
  }, [currentMonth])

  const handleYearSelect = useCallback((year) => {
    const newMonth = new Date(currentMonth)
    newMonth.setFullYear(year)
    setCurrentMonth(newMonth)
    setShowYearPicker(false)
  }, [currentMonth])

  const handleMonthSelect = useCallback((month) => {
    const newMonth = new Date(currentMonth)
    newMonth.setMonth(month)
    setCurrentMonth(newMonth)
    setShowMonthPicker(false)
  }, [currentMonth])

  const handleTodayClick = useCallback(() => {
    const today = getTodayString()
    setSelectedDate(today)
    setIsOpen(false)
    setShowYearPicker(false)
    setShowMonthPicker(false)
    onChange(today)
    const todayDate = new Date()
    setCurrentMonth(new Date(todayDate.getFullYear(), todayDate.getMonth(), 1))
  }, [onChange])

  // Generate year range (current year ± 20 years)
  const getYearRange = useCallback(() => {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let i = currentYear - 20; i <= currentYear + 20; i++) {
      years.push(i)
    }
    return years
  }, [])

  // Get month names in Japanese
  const monthNames = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月']

  // Memoize calendar rendering to prevent unnecessary re-renders
  const renderCalendar = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentMonth)
    const firstDay = getFirstDayOfMonth(currentMonth)
    const days = []

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="w-8 h-8"></div>)
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day)
      const dateString = formatDateToString(date)
      const isSelected = selectedDate === dateString
      const isToday = dateString === getTodayString()

      days.push(
        <button
          key={day}
          onClick={() => handleDateSelect(day)}
          className={`w-8 h-8 rounded text-sm transition-colors flex items-center justify-center ${
            isSelected 
              ? 'bg-red-500 text-white' 
              : isToday 
                ? 'bg-gray-200 text-gray-800' 
                : 'hover:bg-gray-100 text-gray-700'
          }`}
        >
          {day}
        </button>
      )
    }

    return days
  }, [currentMonth, selectedDate, handleDateSelect])

  return (
    <div ref={datePickerRef} className={`relative ${className}`}>
      <div className="flex items-center">
        <input
          type="text"
          value={formatDate(selectedDate)}
          readOnly
          placeholder={placeholder}
          onClick={() => setIsOpen(!isOpen)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent cursor-pointer"
          data-testid="date-input"
        />
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="ml-2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
        >
          📅
        </button>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 p-4 min-w-[280px]">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => handleMonthChange('prev')}
              className="p-1 hover:bg-gray-100 rounded"
              disabled={showYearPicker || showMonthPicker}
            >
              ‹
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setShowYearPicker(!showYearPicker)
                  setShowMonthPicker(false)
                }}
                className="px-2 py-1 hover:bg-gray-100 rounded font-semibold"
                disabled={showMonthPicker}
              >
                {currentMonth.getFullYear()}年
              </button>
              <button
                onClick={() => {
                  setShowMonthPicker(!showMonthPicker)
                  setShowYearPicker(false)
                }}
                className="px-2 py-1 hover:bg-gray-100 rounded font-semibold"
                disabled={showYearPicker}
              >
                {currentMonth.getMonth() + 1}月
              </button>
            </div>
            <button
              onClick={() => handleMonthChange('next')}
              className="p-1 hover:bg-gray-100 rounded"
              disabled={showYearPicker || showMonthPicker}
            >
              ›
            </button>
          </div>

          {/* Year Picker */}
          {showYearPicker && (
            <div className="mb-4 max-h-64 overflow-y-auto border border-gray-200 rounded p-2">
              <div className="grid grid-cols-4 gap-2">
                {getYearRange().map((year) => (
                  <button
                    key={year}
                    onClick={() => handleYearSelect(year)}
                    className={`px-3 py-2 text-sm rounded hover:bg-gray-100 ${
                      year === currentMonth.getFullYear()
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'text-gray-700'
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Month Picker */}
          {showMonthPicker && (
            <div className="mb-4 border border-gray-200 rounded p-2">
              <div className="grid grid-cols-3 gap-2">
                {monthNames.map((month, index) => (
                  <button
                    key={index}
                    onClick={() => handleMonthSelect(index)}
                    className={`px-3 py-2 text-sm rounded hover:bg-gray-100 ${
                      index === currentMonth.getMonth()
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'text-gray-700'
                    }`}
                  >
                    {month}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Calendar Grid - Only show when year/month pickers are closed */}
          {!showYearPicker && !showMonthPicker && (
            <>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['日', '月', '火', '水', '木', '金', '土'].map(day => (
                  <div key={day} className="w-8 h-8 flex items-center justify-center text-xs font-medium text-gray-500">
                    {day}
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 mb-2">
                {renderCalendar}
              </div>
              <div className="flex justify-center mt-2 pt-2 border-t border-gray-200">
                <button
                  onClick={handleTodayClick}
                  className="px-4 py-1 text-sm text-red-500 hover:bg-red-50 rounded transition-colors"
                >
                  今日
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default DatePicker
